import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { HrOverride } from '@app/shared/_models/hr';
import { HR_OVERRIDE_ADD, HR_OVERRIDE_REMOVE } from '@app/shared/constants';
import { minutesLabel, minutesOfHhmm } from '../../hr-schedule-engine';
import { isPlainLeftClick } from '@app/shared/util';

// Dialog d'édition du PLANNING RÉEL pour UN salarié et UN jour.
// Le planning est ce qui est véritablement prévu ce jour-là : les créneaux réguliers (issus des
// horaires) ET les créneaux ajoutés à la main y figurent côte à côte, sans notion d'« exception ».
// Il ne poste RIEN : il rend une action que le planning exécute puis recharge.
//   { kind: 'remove-day', comment }                — le salarié ne travaille pas ce jour
//   { kind: 'add', start_min, end_min, comment }   — ajoute un créneau ce jour
//   { kind: 'delete-override', id }                — retire un créneau ajouté (ou restaure une journée)
// Mode « ajout de salarié » : data.pickEmployee = true ⇒ le salarié (et le jour) se choisissent ici.
// UN LIEU DE TRAVAIL, au sens du planning : un emploi du temps, c'est-à-dire le couple
// établissement × service (« Ollioules — Livraison »). C'est lui que porte un créneau, et donc lui
// qui répond à « elle travaille OÙ, ce jour-là ? ».
export interface OverridePlace {
	id_schedule: number;
	label: string;				// « Ollioules — Livraison »
	store: string;
}

export interface OverrideDialogData {
	employeeName?: string;
	id_employee?: number;
	dat?: string;
	dayLabel?: string;
	scheduleName: string;
	// Créneaux RÉGULIERS (issus des horaires), chacun avec le lieu où il se tient : sur une même
	// journée, le matin peut être à Ollioules et l'après-midi à La Seyne.
	theoreticalSlots: { start_min: number; end_min: number; place?: string }[];
	overrides: HrOverride[];			// modifications déjà enregistrées sur ce jour (ajouts / journée retirée)
	// Lieux où CETTE personne peut travailler (rattachement + mobilité), pour le créneau à ajouter.
	places?: OverridePlace[];
	id_schedule?: number;					// lieu proposé par défaut
	pickEmployee?: boolean;
	employees?: { id: number; name: string }[];	// candidats à l'ajout (mobilité)
	weekDays?: { dat: string; label: string }[];
}

export type OverrideDialogResult =
	| { kind: 'remove-day'; id_employee: number; dat: string; comment: string | null }
	| { kind: 'add'; id_employee: number; dat: string; id_schedule: number; start_min: number; end_min: number; comment: string | null }
	| { kind: 'delete-override'; id: number }
	| undefined;

@Component({
	selector: 'app-hr-override-dialog',
	standalone: true,
	imports: [RouterLink, ReactiveFormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatSelectModule,
		MatButtonModule, MatIconModule, MatTooltipModule],
	templateUrl: './override-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './override-dialog.component.scss',
})
export class OverrideDialogComponent {

	public data: OverrideDialogData = inject(MAT_DIALOG_DATA);
	private dialogRef = inject(MatDialogRef<OverrideDialogComponent>);

	public form = new FormGroup({
		id_employee: new FormControl<number | null>(this.data.id_employee ?? null),
		dat: new FormControl<string | null>(this.data.dat ?? null),
		// OÙ le créneau se tient. Sans ce choix, l'ajout partait en silence sur le premier emploi du
		// temps du magasin affiché : une personne mobile se retrouvait planifiée au mauvais endroit.
		id_schedule: new FormControl<number | null>(this.data.id_schedule ?? this.data.places?.[0]?.id_schedule ?? null),
		start: new FormControl<string>('08:00'),
		end: new FormControl<string>('12:00'),
		comment: new FormControl<string>(''),
	});
	public errorMessage = '';

	get places(): OverridePlace[] {
		return this.data.places ?? [];
	}

	// Lieu d'un créneau déjà enregistré à la main : le même vocabulaire que la liste de choix.
	placeOf(id_schedule: number | null | undefined): string {
		return this.places.find(p => p.id_schedule === id_schedule)?.label ?? '';
	}

	// Créneaux réellement prévus ce jour : réguliers (horaires) puis ajoutés à la main.
	get regularSlots(): { start_min: number; end_min: number; place?: string }[] {
		return this.data.theoreticalSlots;
	}

	get addedSlots(): HrOverride[] {
		return this.data.overrides.filter(o => o.typ === HR_OVERRIDE_ADD);
	}

	// Présente si la journée a été explicitement marquée « ne travaille pas ».
	get dayOff(): HrOverride | null {
		return this.data.overrides.find(o => o.typ === HR_OVERRIDE_REMOVE) ?? null;
	}

	get hasAnySlot(): boolean {
		return this.regularSlots.length > 0 || this.addedSlots.length > 0;
	}

	slotLabel(s: { start_min: number; end_min: number }): string {
		return minutesLabel(s.start_min) + '–' + minutesLabel(s.end_min);
	}

	addedLabel(o: HrOverride): string {
		return minutesLabel(o.start_min ?? 0) + '–' + minutesLabel(o.end_min ?? 0) + (o.comment ? ' · ' + o.comment : '');
	}

	private target(): { id_employee: number; dat: string } | null {
		const id_employee = this.form.get('id_employee')!.value;
		const dat = this.form.get('dat')!.value;
		if (!id_employee || !dat) { this.errorMessage = 'Choisissez le salarié et le jour.'; return null; }
		return { id_employee, dat };
	}

	// Le lieu retenu pour l'ajout. Un créneau doit se tenir QUELQUE PART : sans emploi du temps, le
	// serveur ne saurait ni dans quel magasin ni sur quel service le poser.
	private targetSchedule(): number | null {
		const id = this.form.get('id_schedule')!.value;
		if (!id) { this.errorMessage = 'Choisissez le lieu de travail de ce créneau.'; return null; }
		return id;
	}

	onRemoveDay(): void {
		const t = this.target();
		if (!t) return;
		this.dialogRef.close({ kind: 'remove-day', ...t, comment: this.form.get('comment')!.value?.trim() || null } as OverrideDialogResult);
	}

	onAdd(): void {
		const t = this.target();
		if (!t) return;
		const id_schedule = this.targetSchedule();
		if (id_schedule === null) return;
		const start = minutesOfHhmm(this.form.get('start')!.value);
		const end = minutesOfHhmm(this.form.get('end')!.value);
		if (start === null || end === null || end <= start) { this.errorMessage = 'Horaires invalides (la fin doit être après le début).'; return; }
		this.dialogRef.close({
			kind: 'add', ...t, id_schedule, start_min: start, end_min: end,
			comment: this.form.get('comment')!.value?.trim() || null,
		} as OverrideDialogResult);
	}

	onDeleteOverride(o: HrOverride): void {
		this.dialogRef.close({ kind: 'delete-override', id: o.id } as OverrideDialogResult);
	}

	onCancel(): void {
		this.dialogRef.close(undefined);
	}

	// Le dialogue se referme quand la fiche s'ouvre DANS l'onglet courant : sinon il resterait
	// posé par-dessus la fiche. Ctrl/shift+clic ouvre un onglet à côté : on ne touche à rien.
	onSheetLink(e: MouseEvent): void {
		if (isPlainLeftClick(e)) this.dialogRef.close(undefined);
	}
}
